import React, { useState } from "react";
import styled from "styled-components";
import { auth } from "../../firebase";
import GoBackButton from "./components/GoBackButton";
import MyHeader from "../../components/common/MyHeader";
import {
  PageWrapper, Container, Header, Title, Subtitle,
  Card, Footer, FooterText, FooterLink,
} from './components/setupStyles';


import {
    linkWithPopup,
    unlink,
    EmailAuthProvider,
    GoogleAuthProvider,
    GithubAuthProvider,
} from "firebase/auth";

const providerList = [
    {
        id: "google.com",
        name: "Google",
        provider: new GoogleAuthProvider(),
        color: "#9333ea",        // base
        hoverColor: "#7e22ce",   // hover
        canLink: true,
    },
    {
        id: "password",
        name: "Email & Password",
        provider: EmailAuthProvider,
        color: "#6B7280",
        hoverColor: "#4b5563",
        canLink: false, // ❗ Email/password does NOT support linkWithPopup
    }
];

export default function AuthSetupPage() {
    const [user, setUser] = useState(auth.currentUser);
    const [message, setMessage] = useState("");

    const refreshUser = async () => {
        await user?.reload();
        setUser(auth.currentUser);
    };

    const connectProvider = async (providerConfig) => {
        setMessage("");

        if (!providerConfig.canLink) {
            setMessage("Email/Password cannot be linked via popup.");
            return;
        }

        try {
            await linkWithPopup(auth.currentUser, providerConfig.provider);
            setMessage(`Connected ${providerConfig.name}!`);
            refreshUser();
        } catch (error) {
            console.error(error);

            if (error.code === "auth/credential-already-in-use") {
                setMessage("❗ This provider is already linked to another account.");
            } else if (error.code === "auth/popup-blocked") {
                setMessage("❗ Popup blocked — allow popups and try again.");
            } else {
                setMessage(`❗ Failed to connect ${providerConfig.name}.`);
            }
        }
    };

    const disconnectProvider = async (providerId) => {
        setMessage("");

        if (user.providerData.length <= 1) {
            setMessage("❗ You cannot remove your last login method.");
            return;
        }

        try {
            await unlink(auth.currentUser, providerId);
            setMessage(`Disconnected: ${providerId}`);
            refreshUser();
        } catch (error) {
            console.error(error);
            setMessage("❗ Failed to disconnect provider.");
        }
    };

    const isConnected = (providerId) =>
        user?.providerData?.some((p) => p.providerId === providerId);

    return (

        <>
            <MyHeader />
            <PageWrapper>
                <Container>
                    <GoBackButton />
                    <Header>
                        <Title>Login Setup</Title>
                        <Subtitle>Manage how you log in to your TeamCAST account.</Subtitle>

                    </Header>
                    {message && <MessageBox>{message}</MessageBox>}

                    <Card>
                    <ProviderList>
                        {providerList.map((p) => {
                            const connected = isConnected(p.id);

                            return (
                                <ProviderCard key={p.id}>
                                    <ProviderLeft>
                                        <ProviderTitle>{p.name}</ProviderTitle>

                                        <ProviderStatus connected={connected}>
                                            {connected ? "Connected" : "Not Connected"}
                                        </ProviderStatus>
                                    </ProviderLeft>

                                    {!connected ? (
                                        <ConnectBtn
                                            onClick={() => connectProvider(p)}
                                            disabled={!p.canLink}
                                            color={p.color}
                                            hoverColor={p.hoverColor}
                                        >
                                            Connect
                                        </ConnectBtn>
                                    ) : (
                                        <DisconnectBtn
                                            onClick={() => disconnectProvider(p.id)}
                                            disabled={user?.providerData?.length <= 1}
                                        >
                                            Disconnect
                                        </DisconnectBtn>
                                    )}
                                </ProviderCard>
                            );
                        })}
                    </ProviderList>
                    </Card>
                    <Footer>
                        <FooterText>
                            Need help? Check out our{" "}
                            <FooterLink
                                href="https://docs.google.com/document/d/10ktlofLuFkFbs9ZEEs2jwpvycE6nz8KBXZyE4FJ8w40/edit?usp=sharing"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                documentation
                            </FooterLink>
                        </FooterText>
                    </Footer>
                </Container>
            </PageWrapper>
        </>
    );
}

/* ---------------------------------- STYLES ---------------------------------- */

const ProviderList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ProviderCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 0;
  border-bottom: 1px solid #e5e7eb;
  &:last-of-type {
    border-bottom: none;
    padding-bottom: 0;
  }
`;

const ProviderLeft = styled.div`
  display: flex;
  align-items: flex-start;  
  flex-direction: column;
  gap: 0.25rem;
`;

const ProviderTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: #111827;
`;

const ProviderStatus = styled.p.withConfig({
    shouldForwardProp: (p) => p !== "connected",
})`
  font-size: 0.875rem;
  font-weight: 500;
  margin-top: 0.25rem;
  color: ${(p) => (p.connected ? "#16a34a" : "#dc2626")};
`;

const ConnectBtn = styled.button`
  padding: 0.6rem 1.25rem;
  font-weight: 600;
  border-radius: 0.6rem;
  color: white;
  border: none;
  cursor: pointer;
  transform: scale(1);
  transition: background 0.2s, transform 0.15s ease;

  background: ${(p) => p.color};
  
  &:hover {
    background: ${(p) => p.hoverColor};
    transform: scale(1.05);
  }

  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
  }
`;

const DisconnectBtn = styled.button`
  padding: 0.6rem 1.25rem;
  font-weight: 600;
  border-radius: 0.6rem;
  border: none;
  cursor: pointer;

  background: rgba(239, 68, 68, 0.12);
  color: #dc2626;
  transform: scale(1);
  transition: background 0.2s, transform 0.15s ease;

  &:hover {
    background: rgba(239, 68, 68, 0.2);
    transform: scale(1.05);
  }

  &:disabled {
    background: rgba(239, 68, 68, 0.08);
    color: #fca5a5;
    cursor: not-allowed;
  }

  
`;

const MessageBox = styled.div`
  background: #eef2ff;
  color: #9333ea;
  padding: 12px 16px;
  margin-bottom: 1rem;
  border-radius: 8px;
  border: 1px solid #c7d2fe;
`;


